/**
 * Dates on the Fravær screens as a Norwegian reader writes them: "20.–22. sep. 2026", not
 * 20.9–22.9. The en dash between the two ends is Norwegian typography, unspaced between two
 * numbers and spaced when an end has words in it ("28. sep. – 2. okt.").
 */
import type { ApiAbsence } from '@/api/data';
import { DN } from '@/ui/format';
import { dow } from '@timefly/core';
import { rangeOf } from './AbsenceRange';

const MON = ['jan.', 'feb.', 'mars', 'apr.', 'mai', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'];
const d = (x: string) => Number(x.slice(8, 10));
const m = (x: string) => MON[Number(x.slice(5, 7)) - 1] ?? '';
const y = (x: string) => x.slice(0, 4);

/** The Oslo dates an absence covers, end date included (ruling 2). */
export const spanOf = (a: Pick<ApiAbsence, 'start_date' | 'end_date'>) => {
  const r = rangeOf(a);
  return { from: r.from, to: r.to || r.from };
};

/** "20. sep. 2026", "20.–22. sep. 2026", "28. sep. – 2. okt. 2026", across a new year both years. */
export function periodText(from: string, to: string, withYear = true): string {
  const yr = (x: string) => (withYear ? ` ${y(x)}` : '');
  if (from === to) return `${d(from)}. ${m(from)}${yr(from)}`;
  if (y(from) !== y(to)) return `${d(from)}. ${m(from)} ${y(from)} – ${d(to)}. ${m(to)} ${y(to)}`;
  if (m(from) === m(to)) return `${d(from)}.–${d(to)}. ${m(to)}${yr(to)}`;
  return `${d(from)}. ${m(from)} – ${d(to)}. ${m(to)}${yr(to)}`;
}

/** One day with its weekday: "Man 21. sep." */
export const dayText = (x: string) => `${DN[dow(x)]} ${d(x)}. ${m(x)}`;

/** The weekdays of the two ends: "Søn 20. sep. – Tir 22. sep.", or one day. */
export const weekdaySpan = (from: string, to: string) =>
  from === to ? dayText(from) : `${dayText(from)} – ${dayText(to)}`;

/** "8. sep. 2026 kl. 10:24" for an instant already read in Oslo. */
export const stampText = (o: { date: string; time: string } | null) =>
  o ? `${d(o.date)}. ${m(o.date)} ${y(o.date)} kl. ${o.time}` : '';
